/**
 * Textes et petits calculs de l'onglet « Pièces reçues », sans DOM : libellés,
 * messages de refus en français, correction à envoyer d'après le formulaire.
 * Séparés de l'écran pour être éprouvés seuls.
 */

import type { ChampPiece, CorrectionPiece, EtatPiece, NaturePiece, Piece, SaisieRegleCollecte } from './api.js';

export const NATURES: Readonly<Record<NaturePiece, string>> = {
  facture_scc: 'Facture reçue par le SCC',
  autre_structure: 'Facture d’une autre structure',
  facture_emise: 'Facture émise par le SCC',
  pas_une_facture: 'Pas une facture',
  douteuse: 'À trancher',
};

export const ETATS_PIECE: Readonly<Record<EtatPiece, string>> = {
  a_valider: 'À valider',
  a_arbitrer: 'À arbitrer',
  a_telecharger: 'À télécharger',
  deposee: 'Déposée dans le Drive',
  deja_classee: 'Déjà classée',
  ecartee: 'Écartée',
  retiree: 'Retirée',
};

/** Pourquoi une pièce est écartée, proposé comme motif par défaut selon la décision prise. */
export const MOTIFS_PAR_DEFAUT: Readonly<Record<'autre_structure' | 'facture_emise' | 'pas_une_facture', string>> = {
  autre_structure: 'Facture adressée à une autre structure',
  facture_emise: 'Copie d’une facture émise par le SCC',
  pas_une_facture: 'Ce n’est pas une facture',
};

const PROVENANCES: Readonly<Record<string, string>> = {
  regle: 'Imposé par une règle de collecte',
  texte: 'Lu dans le texte de la pièce',
  courriel: 'Tiré du courriel',
  saisie: 'Saisi par une personne',
};

/** Infobulle d'un champ : d'où vient la valeur, et s'il faut la vérifier. */
export function infobulleChamp(c: ChampPiece<unknown> | undefined): string {
  if (c === undefined || c.valeur === null) return 'Non lu : à saisir.';
  const origine = c.provenance === null ? 'Origine inconnue' : (PROVENANCES[c.provenance] ?? c.provenance);
  return c.douteux ? `${origine}. Lecture douteuse : à vérifier.` : `${origine}.`;
}

/** « 12 Ko », « 1,4 Mo ». */
export function tailleFr(octets: number): string {
  if (octets < 1024) return `${String(octets)} o`;
  if (octets < 1024 * 1024) return `${String(Math.round(octets / 1024))} Ko`;
  return `${(octets / (1024 * 1024)).toFixed(1).replace('.', ',')} Mo`;
}

/** Les critères renseignés d'une règle, en toutes lettres. */
export function criteresEnMots(r: Pick<SaisieRegleCollecte, 'expediteur' | 'boite' | 'sujetContient' | 'fichierContient'>): string {
  const morceaux: string[] = [];
  if (r.expediteur) morceaux.push(`expéditeur ${r.expediteur}`);
  if (r.boite) morceaux.push(`boîte ${r.boite}`);
  if (r.sujetContient) morceaux.push(`sujet contenant « ${r.sujetContient} »`);
  if (r.fichierContient) morceaux.push(`nom de fichier contenant « ${r.fichierContient} »`);
  return morceaux.length === 0 ? 'aucun critère' : morceaux.join(', ');
}

/** L'effet d'une règle, en toutes lettres. */
export function effetEnMots(r: Pick<SaisieRegleCollecte, 'nature' | 'fournisseur' | 'rubrique' | 'depotAuto' | 'jamaisJev'>): string {
  const morceaux: string[] = [NATURES[r.nature]];
  if (r.fournisseur) morceaux.push(`fournisseur ${r.fournisseur}`);
  if (r.rubrique) morceaux.push(`rubrique ${r.rubrique}`);
  if (r.depotAuto) morceaux.push('déposée sans validation');
  if (r.jamaisJev) morceaux.push('jamais envoyée à Jev');
  return morceaux.join(', ');
}

const MOTIFS_IGNOREE: Readonly<Record<string, string>> = {
  incorporee: 'incorporées au message',
  type_non_admis: 'd’un type non admis',
  image_trop_petite: 'images de moins de 5 Ko',
  vide: 'vides',
  trop_volumineuse: 'de plus de 20 Mo dans une archive',
};

/** Les pièces jointes laissées de côté par une campagne, en toutes lettres ; `null` s'il n'y en a aucune. */
export function ignoreesEnMots(ignorees: Readonly<Record<string, number>> | undefined): string | null {
  const morceaux = Object.entries(ignorees ?? {})
    .filter(([, n]) => n > 0)
    .map(([motif, n]) => `${MOTIFS_IGNOREE[motif] ?? motif} : ${String(n)}`);
  return morceaux.length === 0 ? null : `Pièces jointes laissées de côté : ${morceaux.join(', ')}`;
}

/** Ce que la personne a saisi dans la fiche d'une pièce à valider. */
export interface SaisiePiece {
  readonly nature: NaturePiece;
  readonly fournisseur: string;
  readonly date: string;
  readonly montant: string;
  readonly devise: string;
  readonly numero: string;
  readonly rubrique: string;
  readonly nom: string;
}

const net = (v: string): string | null => (v.trim() === '' ? null : v.trim());

/** Un montant saisi à la française (« 1 234,50 ») ; `null` s'il est vide, `NaN` s'il est illisible. */
export function montantSaisi(v: string): number | null {
  const t = v.replace(/\s/g, '').replace(',', '.');
  if (t === '') return null;
  return /^-?\d+(\.\d{1,2})?$/.test(t) ? Number(t) : Number.NaN;
}

/**
 * Ce qui change entre la pièce et la saisie, au format du serveur : seules les
 * clés modifiées sont envoyées. `correction` vide : rien à enregistrer.
 */
export function correctionDepuis(p: Piece, s: SaisiePiece): { correction: CorrectionPiece } | { erreur: string } {
  const correction: CorrectionPiece = {};
  const champs: NonNullable<CorrectionPiece['champs']> = {};
  const c = p.champs;

  if (s.nature !== p.nature) correction.nature = s.nature;

  const fournisseur = net(s.fournisseur);
  if (fournisseur !== (c?.fournisseur.valeur ?? null)) champs.fournisseur = fournisseur;
  const date = net(s.date);
  if (date !== (c?.date.valeur ?? null)) champs.date = date;
  const numero = net(s.numero);
  if (numero !== (c?.numero.valeur ?? null)) champs.numero = numero;
  const devise = net(s.devise)?.toUpperCase() ?? null;
  if (devise !== (c?.devise.valeur ?? null)) {
    if (devise !== null && !/^[A-Z]{3}$/.test(devise)) return { erreur: 'La devise s’écrit avec trois lettres, par exemple EUR.' };
    champs.devise = devise;
  }
  const montant = montantSaisi(s.montant);
  if (Number.isNaN(montant)) return { erreur: 'Le montant est illisible : des chiffres, avec une virgule ou un point pour les centimes.' };
  if (montant !== (c?.montant.valeur ?? null)) champs.montant = montant;
  if (Object.keys(champs).length > 0) correction.champs = champs;

  const rubrique = net(s.rubrique);
  if (rubrique !== p.rubrique) correction.rubrique = rubrique;

  const nom = net(s.nom);
  if (nom !== null && nom !== (p.nomPropose ?? '')) correction.nom = nom;

  return { correction };
}

/** Message d'un dépôt dans le Drive refusé ; le refus du mode à blanc est expliqué en détail. */
export function messageDepotRefuse(motif: string | undefined, messageServeur: string): string {
  if (motif === 'depot_inactif') {
    return (
      'Mode à blanc : le dépôt dans le Drive n’est pas activé, rien n’a été déposé. ' +
      'La pièce reste à valider. Le dépôt s’ouvre dans les réglages du module Justificatifs (« Déposer les pièces dans le Drive »).'
    );
  }
  return messageServeur;
}

/** Message d'un fichier refusé au dépôt, d'après le statut de la réponse. */
export function messageEnvoiRefuse(statut: number, messageServeur: string): string {
  switch (statut) {
    case 413:
      return 'Fichier trop volumineux : 20 Mo au plus.';
    case 415:
      return 'Type de fichier non admis : PDF, JPEG, PNG ou HEIC seulement.';
    case 409:
      return 'Cette demande n’attend plus de fichier : elle a déjà été traitée. Actualisez la page.';
    case 404:
      return 'Cette demande de téléchargement n’existe plus. Actualisez la page.';
    default:
      return messageServeur;
  }
}

/** Contrôle fait avant l'envoi : le serveur refuserait de toute façon, mieux vaut ne pas envoyer 20 Mo pour rien. */
export function refusAvantEnvoi(fichier: { readonly size: number } | undefined, max: number): string | null {
  if (fichier === undefined) return 'Choisissez d’abord un fichier.';
  if (fichier.size === 0) return 'Ce fichier est vide.';
  if (fichier.size > max) return 'Fichier trop volumineux : 20 Mo au plus.';
  return null;
}
